import type { ReactNode } from 'react';
import type { Tone } from '@shared/format';

/** 与共享层的 `Tone` 是同一个联合：状态、研判结论、标签配色都复用它。 */
export type BadgeTone = Tone;

const TONES: Record<BadgeTone, string> = {
  neutral: 'border-line bg-surface-3 text-fg-muted',
  accent: 'border-accent/40 bg-accent-soft text-accent',
  ok: 'border-ok/40 bg-ok-soft text-ok',
  info: 'border-info/40 bg-info-soft text-info',
  caution: 'border-caution/50 bg-caution-soft text-caution',
  warning: 'border-warning/50 bg-warning-soft text-warning',
  critical: 'border-critical/50 bg-critical-soft text-critical',
};

/*
 * 实心底色的前景色统一用 `text-fg-inverted`：
 * 浅色主题下深色底配白字，深色主题下亮色底配近黑字。
 * 固定的 `text-white` 在深色主题里会掉到 2.8:1（例如亮红 bg-critical），
 * 达不到 WCAG AA 的 4.5:1，告警色尤其不能这样用。
 */
const SOLID: Record<BadgeTone, string> = {
  neutral: 'border-transparent bg-fg-muted text-surface-0',
  accent: 'border-transparent bg-accent text-accent-fg',
  ok: 'border-transparent bg-ok text-fg-inverted',
  info: 'border-transparent bg-info text-fg-inverted',
  caution: 'border-transparent bg-caution text-fg-inverted',
  warning: 'border-transparent bg-warning text-fg-inverted',
  critical: 'border-transparent bg-critical text-fg-inverted',
};

export interface BadgeProps {
  tone?: BadgeTone;
  children: ReactNode;
  /** 用实心底色强调，用于需要一眼看到的场景（例如结论为「不建议推进」） */
  solid?: boolean;
  className?: string;
  title?: string;
}

export function Badge({ tone = 'neutral', children, solid = false, className = '', title }: BadgeProps) {
  return (
    <span
      title={title}
      className={[
        'inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] leading-4 font-medium whitespace-nowrap',
        solid ? SOLID[tone] : TONES[tone],
        className,
      ].join(' ')}
    >
      {children}
    </span>
  );
}

/** 水平进度条：用于五维评分这类归一化到 0–100 的指标。 */
export function QualityBar({
  value,
  min = 0,
  max = 100,
  tone = 'ok',
  label,
  ariaLabel,
}: {
  value: number;
  min?: number;
  max?: number;
  tone?: BadgeTone;
  label?: string;
  /** 无障碍名称；用于进度条旁已单独显示数值、不需要再渲染 `label` 的场景 */
  ariaLabel?: string;
}) {
  const ratio = max === min ? 0 : Math.min(1, Math.max(0, (value - min) / (max - min)));
  return (
    <div className="flex items-center gap-2" title={label}>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-surface-3"
        role="progressbar"
        aria-label={ariaLabel ?? label}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={Math.round(value)}
      >
        <div className={['h-full rounded-full', SOLID[tone]].join(' ')} style={{ width: `${Math.round(ratio * 100)}%` }} />
      </div>
      {label ? <span className="tabular shrink-0 text-[11px] text-fg-muted">{label}</span> : null}
    </div>
  );
}
